// URL-state schema for RFC-102 (4D + scenario + URL encoding).
// See: geosoft-platform-strategy/docs/appendix/rfcs/RFC-102-4d-timeline-scenario-state-model.md
//
// Two on-the-wire payload shapes:
//
//   ViewerState        — the in-app shape consumers work with (rich names).
//   UrlViewState       — the compact base64-payload shape (short keys to
//                        meet the 1.8 KB budget per RFC-102 P3).
//
// encodeView() converts ViewerState -> UrlViewState (compact) -> base64 string;
// decodeView() reverses it. The schemaVersion (`v: 1`) lets future RFC-102
// amendments evolve the wire format without breaking older clients.

import type { EngineId } from '@geosoft/viewer-sdk';

export const URL_STATE_SCHEMA_VERSION = 1;

// ---------- in-app shape ----------

export type ViewTime = Date | null;

export interface ViewState {
  /** [longitude, latitude] */
  center?: [number, number];
  zoom?: number;
  heading?: number;
  pitch?: number;
}

export interface ScenarioOverrides {
  style?: Record<string, unknown>;
  visibility?: { minZoom?: number; maxZoom?: number };
}

export interface Scenario {
  id: string;
  label: string;
  layerIds: ReadonlyArray<string>;
  layerOverrides?: Record<string, ScenarioOverrides>;
  pinnedTime?: ViewTime;
  notes?: string;
}

export interface ViewerState {
  view?: ViewState;
  time?: ViewTime;
  visibleLayerIds?: ReadonlyArray<string>;
  primaryEngine?: EngineId;
  scenarios?: ReadonlyArray<Scenario>;
  activeScenarioId?: string | null;
  compareWithScenarioId?: string | null;
  /** Active organization alias (RFC-106 P5 'o' field). */
  orgAlias?: string;
  /** Optional feature-flag overrides (ignored if URL is read-only tier). */
  featureFlags?: Record<string, boolean>;
}

// ---------- compact wire shape (RFC-102 P3) ----------

export interface CompactScenario {
  id: string;
  lab: string;
  lids: string[];
  pt?: string | null;
  lov?: Record<string, { st?: unknown; vis?: { mn?: number; mx?: number } }>;
  n?: string;
}

export interface UrlViewState {
  v: 1;
  c?: [number, number];
  z?: number;
  h?: number;
  p?: number;
  t?: string | null;
  l?: string[];
  e?: EngineId;
  sc?: CompactScenario[];
  sa?: string | null;
  cmp?: string | null;
  o?: string;
  fl?: Record<string, boolean>;
}

// ---------- decode result ----------

export interface DecodedView {
  state: ViewerState;
  readOnly: boolean;
  /** Layer IDs that were in the URL but the consumer should drop (e.g. unknown). */
  droppedLayerIds?: ReadonlyArray<string>;
}

// ---------- byte-budget constants (RFC-102 P3) ----------

/** Encoder warns above this. */
export const URL_PAYLOAD_WARN_BYTES = 1500;
/** Encoder refuses above this. */
export const URL_PAYLOAD_HARD_LIMIT_BYTES = 1800;

// ---------- hash-fragment prefixes (RFC-102 P4) ----------

export const HASH_PREFIX_EDITABLE = '#view=';
export const HASH_PREFIX_READONLY = '#viewro=';
